import { sentenceCase } from 'change-case';
import { useCallback, useEffect, useState } from 'react';
// material
import {
  Card,
  Checkbox,
  Container,
  Stack,
  Table,
  TableBody,
  TableCell,
  tableCellClasses,
  TableContainer,
  TableRow,
  Typography,
  Select,
  MenuItem,
  IconButton
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useDispatch } from 'react-redux';
import { Box } from '@mui/system';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

// components
import BrudCrumbs from '../../components/BreadCrumbs';
import Label from '../../components/Label';
import Page from '../../components/Page';
import Scrollbar from '../../components/Scrollbar';
import SearchNotFound from '../../components/SearchNotFound';
import { UserListHead, UserMoreMenu } from '../../components/_dashboard/user';
import { fDate, fDateTime2 } from '../../utils/formatTime';
import RejectedToolbar from './RejectedToolbar';
import { NOT_AVAILABLE } from '../Global/Constants';
import useLoginSelector from '../../store/Slice/Login/LoginSelector';
import { getRejectedList } from '../../store/Slice/Rejected/RejectedSlice';
import useRejectedSelector from '../../store/Slice/Rejected/RejectedSelector';
import { isValidValue, normalizeValue, truncateText } from '../../utils/CommonFunction';
import useDebounce from '../../utils/useDebounce';

const TABLE_HEAD = [
  { id: 'leadId', label: 'APPLICATION ID', alignRight: false },
  { id: 'lanId', label: 'LAN ID', alignRight: false },
  { id: 'created', label: 'SUBMISSION DATE', alignRight: false },
  { id: 'name', label: 'NAME', alignRight: false },
  { id: 'companyName', label: 'COMPANY NAME', alignRight: false },
  { id: 'contactNumber', label: 'CONTACT NUMBER', alignRight: false },
  { id: 'sourceFlag', label: 'FILE SOURCE', alignRight: false },
  { id: 'loanType', label: 'PRODUCT', alignRight: false },
  { id: 'city', label: 'CITY', alignRight: false },
  { id: 'amount', label: 'AMOUNT REQUESTED', alignRight: false },
  { id: 'bankName', label: 'BANK', alignRight: false },
  { id: 'dsaName', label: 'DSA NAME', alignRight: false },
  { id: 'status', label: 'STATUS', alignRight: false },
  { id: 'reason', label: 'REASON', alignRight: false },
  { id: 'lastUpdatedDate', label: 'LAST UPDATED', alignRight: false },
  { id: 'action', label: 'ACTION', alignRight: false, align: 'center' }
];

const StyledTableRow = styled(TableRow)(() => ({
  backgroundColor: '#fff',
  '&:nth-of-type(even)': {
    backgroundColor: '#fafbfc'
  },
  '&:hover': {
    backgroundColor: '#f1f5f9 !important'
  },
  '&:last-child td, &:last-child th': {
    border: 0
  }
}));

const StyledTableCell = styled(TableCell)(() => ({
  [`&.${tableCellClasses.head}`]: {
    fontSize: 11.5,
    fontWeight: 700,
    padding: '12px 14px',
    color: '#64748b',
    textTransform: 'uppercase',
    backgroundColor: '#ffffff',
    borderBottom: '1px solid #e2e8f0',
    letterSpacing: '0.5px'
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 13,
    padding: '12px 14px',
    borderBottom: '1px solid #f1f5f9',
    color: '#334155'
  }
}));

export default function RejectedList() {
  const [order, setOrder] = useState('desc');
  const [selected, setSelected] = useState([]);
  const [orderBy, setOrderBy] = useState();
  const [filterName, setFilterName] = useState('');
  const [filterState, setFilterState] = useState({
    product: ' ',
    productId: null,
    productArray: [],
    city: null,
    cityId: null,
    cityArray: [],
    status: ' ',
    statusId: null,
    statusArray: [],
    employmentType: ' ',
    employmentTypeId: null,
    employmentTypeArray: [],
    reason: null,
    bankId: null,
    loanTypeId: null,
    rejectedReasonTypeId: null
  });

  const [filterDatesRange, setFilterDatesRange] = useState([
    {
      startDate: null,
      endDate: null,
      key: 'selection'
    }
  ]);

  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(10);
  const [hasNextPage, setHasNextPage] = useState(true);

  const dispatch = useDispatch();
  const { LoginData } = useLoginSelector();
  const { RejectedListData } = useRejectedSelector();
  const debouncedSearch = useDebounce(filterName, 600);

  const getRejectedListApi = useCallback(() => {
    const credentials = {
      companyId: LoginData?.companyId,
      userId: LoginData?.userId,
      accessLevelId: LoginData?.accessLevelId,
      stageId: 58,
      filterModel: {
        bankId: filterState.bankId || null,
        loanTypeId: filterState.loanTypeId || null,
        cityId: filterState.cityId || null,
        rejectedReasonTypeId: filterState.rejectedReasonTypeId || filterState.statusId || null
      },
      entityListRequestModel: {
        searchLiteral: debouncedSearch?.length >= 2 ? debouncedSearch : null,
        sortOrder: order || null
      }
    };
    dispatch(getRejectedList(credentials));
  }, [
    LoginData,
    filterState,
    debouncedSearch,
    order,
    filterDatesRange,
    dispatch,
    page,
    rowsPerPage
  ]);

  useEffect(() => {
    getRejectedListApi();
  }, [getRejectedListApi]);

  useEffect(() => {
    const selectedItem = localStorage.getItem('selectedItem');
    if (selectedItem !== null) {
      localStorage.removeItem('selectedItem');
    }
  }, []);

  const handleFilterByName = (event) => {
    setFilterName(event.target.value);
  };

  const handleRequestSort = (event, property) => {
    if (property !== 'action') {
      setOrder(orderBy === property && order === 'asc' ? 'desc' : 'asc');
      setOrderBy(property);
    }
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = RejectedListData?.map((n) => n);
      setSelected(newSelecteds || []);
      return;
    }
    setSelected([]);
  };

  const handleClick = (event, item) => {
    const selectedIndex = selected.indexOf(item);
    let newSelected = [];
    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, item);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }
    setSelected(newSelected);
  };

  return (
    <Page title="Partner Management System || Rejected Cases">
      <Container maxWidth="xl" sx={{ pb: 6, pt: 2 }}>
        {/* Top Header Row with Icon, Title, and Breadcrumb */}
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          justifyContent="space-between"
          sx={{ mb: 3.5, gap: 2 }}
        >
          <Stack direction="row" spacing={2} alignItems="center">
            <Box
              sx={{
                width: 46,
                height: 46,
                borderRadius: '12px',
                bgcolor: '#fee2e2',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#dc2626'
              }}
            >
              <CancelOutlinedIcon sx={{ fontSize: 26 }} />
            </Box>
            <Box>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  color: '#0f172a',
                  fontSize: { xs: '20px', sm: '24px' },
                  letterSpacing: '-0.3px',
                  mb: 0.25
                }}
              >
                Rejected
              </Typography>
              <Typography variant="body2" sx={{ color: '#64748b', fontSize: '13px' }}>
                Review rejected applications, failure reasons, and logs.
              </Typography>
            </Box>
          </Stack>

          <Box>
            <BrudCrumbs />
          </Box>
        </Stack>

        <Card
          sx={{
            borderRadius: '16px',
            border: '1px solid #e2e8f0',
            boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
            overflow: 'hidden',
            bgcolor: '#ffffff'
          }}
        >
          <RejectedToolbar
            numSelected={selected.length}
            filterName={filterName}
            setFilterName={setFilterName}
            onFilterName={handleFilterByName}
            filterDatesRange={filterDatesRange}
            setFilterDatesRange={setFilterDatesRange}
            filterState={filterState}
            setFilterState={setFilterState}
            parent="rejected"
            selected={selected}
          />

          <Scrollbar>
            <TableContainer sx={{ minWidth: 800, minHeight: 380, overflow: 'auto' }}>
              <Table>
                <UserListHead
                  order={order}
                  orderBy={orderBy}
                  headLabel={TABLE_HEAD}
                  rowCount={RejectedListData?.length || 0}
                  numSelected={selected.length}
                  onRequestSort={handleRequestSort}
                  onSelectAllClick={handleSelectAllClick}
                  isCheckBox="true"
                  parent="rejected"
                />
                <TableBody>
                  {RejectedListData?.map((item, index) => {
                    const isItemSelected = selected.indexOf(item) !== -1;
                    return (
                      <StyledTableRow
                        hover
                        key={item.applicationId || index}
                        tabIndex={-1}
                        role="checkbox"
                        selected={isItemSelected}
                        aria-checked={isItemSelected}
                      >
                        <TableCell padding="checkbox" sx={{ pl: 2.5 }}>
                          <Checkbox
                            checked={isItemSelected}
                            onChange={(event) => handleClick(event, item)}
                            sx={{
                              color: '#cbd5e1',
                              '&.Mui-checked, &.MuiCheckbox-indeterminate': { color: '#0284c7' }
                            }}
                          />
                        </TableCell>
                        <StyledTableCell align="left">
                          <Typography
                            variant="subtitle2"
                            sx={{
                              fontWeight: 600,
                              color: '#0284c7',
                              fontSize: '13px'
                            }}
                          >
                            {normalizeValue(item?.applicationId, NOT_AVAILABLE)}
                          </Typography>
                        </StyledTableCell>
                        <StyledTableCell align="left" sx={{ color: '#64748b' }}>
                          {normalizeValue(item?.lanId, NOT_AVAILABLE)}
                        </StyledTableCell>
                        <StyledTableCell align="left" sx={{ color: '#64748b' }}>
                          {isValidValue(item?.submissionDate)
                            ? fDate(item.submissionDate)
                            : NOT_AVAILABLE}
                        </StyledTableCell>
                        <StyledTableCell align="left" sx={{ fontWeight: 600, color: '#0f172a' }}>
                          {normalizeValue(item?.name, NOT_AVAILABLE)}
                        </StyledTableCell>
                        <StyledTableCell align="left" sx={{ color: '#64748b' }}>
                          {normalizeValue(item?.companyName, NOT_AVAILABLE)}
                        </StyledTableCell>
                        <StyledTableCell align="left" sx={{ color: '#64748b' }}>
                          {normalizeValue(item?.contactNumber, NOT_AVAILABLE)}
                        </StyledTableCell>
                        <StyledTableCell align="left">
                          <Label
                            variant="ghost"
                            color={
                              item?.sourceFlag === 'LOGIN_FILE' ||
                              item?.logInStatusName === 'Rejected'
                                ? 'warning'
                                : 'primary'
                            }
                            sx={{ opacity: 0.8, fontWeight: 500 }}
                          >
                            {normalizeValue(item?.sourceFlag, NOT_AVAILABLE)}
                          </Label>
                        </StyledTableCell>
                        <StyledTableCell align="left" sx={{ color: '#64748b' }}>
                          {normalizeValue(item?.loanTypeName, NOT_AVAILABLE)}
                        </StyledTableCell>
                        <StyledTableCell align="left" sx={{ color: '#64748b' }}>
                          {truncateText(normalizeValue(item?.cityName, NOT_AVAILABLE), 12)}
                        </StyledTableCell>
                        <StyledTableCell align="left" sx={{ color: '#0f172a', fontWeight: 600 }}>
                          {normalizeValue(item?.amount, NOT_AVAILABLE)}
                        </StyledTableCell>
                        <StyledTableCell align="left" sx={{ color: '#64748b' }}>
                          {normalizeValue(item?.bankName, NOT_AVAILABLE)}
                        </StyledTableCell>
                        <StyledTableCell align="left" sx={{ color: '#64748b' }}>
                          {normalizeValue(item?.dsaName, NOT_AVAILABLE)}
                        </StyledTableCell>
                        <StyledTableCell align="left">
                          <Label
                            variant="ghost"
                            color={
                              item?.statusName === 'Closed' || item?.statusName === 'Rejected'
                                ? 'error'
                                : 'warning'
                            }
                          >
                            {isValidValue(item?.statusName)
                              ? sentenceCase(item.statusName)
                              : NOT_AVAILABLE}
                          </Label>
                        </StyledTableCell>
                        <StyledTableCell align="left" sx={{ color: '#64748b' }}>
                          {isValidValue(item?.reason) ? item.reason : NOT_AVAILABLE}
                        </StyledTableCell>
                        <StyledTableCell align="left" sx={{ color: '#64748b' }}>
                          {isValidValue(item?.lastUpdatedDate)
                            ? fDate(item.lastUpdatedDate)
                            : NOT_AVAILABLE}
                        </StyledTableCell>
                        <StyledTableCell align="center">
                          <UserMoreMenu rowId={item} parent="rejected" />
                        </StyledTableCell>
                      </StyledTableRow>
                    );
                  })}
                </TableBody>
                {RejectedListData?.length === 0 && (
                  <TableBody>
                    <TableRow>
                      <StyledTableCell align="center" colSpan={16} sx={{ py: 3 }}>
                        <SearchNotFound searchQuery={filterName} />
                      </StyledTableCell>
                    </TableRow>
                  </TableBody>
                )}
              </Table>
            </TableContainer>
          </Scrollbar>

          {/* Modern Pagination Footer */}
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems="center"
            justifyContent="space-between"
            sx={{ p: 2, px: 3, borderTop: '1px solid #f1f5f9', bgcolor: '#ffffff', gap: 2 }}
          >
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <Typography sx={{ fontSize: '13px', color: '#64748b' }}>Showing</Typography>
              <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#0f172a' }}>
                {RejectedListData?.length || 0} records
              </Typography>
            </Stack>

            <Stack direction="row" alignItems="center" spacing={2}>
              <Typography sx={{ fontSize: '13px', color: '#64748b' }}>
                Page <span style={{ fontWeight: 600, color: '#0f172a' }}>{page + 1}</span>
              </Typography>

              <Stack direction="row" spacing={1}>
                <IconButton
                  disabled={page === 0}
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  sx={{
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    p: 0.75,
                    bgcolor: '#ffffff',
                    color: '#0f172a',
                    '&.Mui-disabled': { opacity: 0.4, bgcolor: '#f8fafc' }
                  }}
                >
                  <ChevronLeftIcon sx={{ fontSize: 18 }} />
                </IconButton>
                <IconButton
                  disabled={!hasNextPage}
                  onClick={() => setPage((p) => p + 1)}
                  sx={{
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    p: 0.75,
                    bgcolor: '#ffffff',
                    color: '#0f172a',
                    '&.Mui-disabled': { opacity: 0.4, bgcolor: '#f8fafc' }
                  }}
                >
                  <ChevronRightIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Stack>
            </Stack>
          </Stack>
        </Card>
      </Container>
    </Page>
  );
}
